(function (root) {
  'use strict';

  /*
   * Mix & Match MultiBuy v3: "pick how many, then choose which".
   *
   * The discount itself is applied by the multibuy-discount Shopify Function.
   * This file previews it with the Function's own maths so the PDP, the cart
   * drawer and checkout agree to the cent:
   *
   *   tierMode "all_units" (the open-ended "3+ kits" offer): the highest tier
   *   the cart + the kits being added reach sets one % for EVERY unit, rounded
   *   per unit (half-up, to the cent). 4 x $68 at 17% = 4 x $56.44.
   *
   *   otherwise: the v2 greedy slices (most expensive first, largest tier that
   *   fits, rounded once per slice), spread across the covered units by price.
   *
   * The block has no add button of its own: the theme's Add to cart (and the
   * sticky bar's) stays the only one. At 1 kit it adds this product exactly as
   * the theme does; at 2+ kits (or with add-ons picked) it adds the slots and
   * the add-ons in one /cart/add.js request.
   *
   * Kit slot control: "+ Add another kit" sits under the last kit from 2 kits
   * up to config.maxSlots (block setting, 3-5). It adds a slot, so the rows
   * move up (2 -> 3 -> 4+), and opens the Swap kit list for the new kit. Any
   * kit from Kit 2 can be removed with × (the rows move back down).
   *
   * Saved selection: 2+ kits are kept in localStorage for 24h (per group), so
   * a kit's full product page (new tab) reopens with that kit as Kit 1 and the
   * other kits after it. It is the same bundle as the PLP tray / sidebar
   * ("Your kits"): on ANY kit page of the group the saved kits are shown in
   * "Your N kits" straight away (this page's kit as Kit 1, the saved kits
   * after it), and the box follows changes made in another tab or on a page
   * reached with back / forward. "Continue" is only offered when the box is
   * set back to 1 kit. The announcement bar counts the saved kits too (event
   * mm3:selection).
   *
   * Add-ons (group "addons" rule): regular price below addons.minKits kits
   * (cart + slots), addons.pct % off each unit (the Function's per-unit
   * rounding) from there. Unlimited, and they never count as kits.
   *
   * window.SculpdMixMatch exposes the pricing core and the membership helper
   * for a later announcement bar / collection badge.
   */

  var MAX_TIER_PASSES = 50;
  var FORM_SELECTOR = 'form.product-single__form, form[action*="/cart/add"]';
  var STICKY_TEXT = 'product-sticky-bar [data-ref="addToCartText"]';
  var STICKY_PRICE = 'product-sticky-bar [data-ref="addToCartPrice"]';
  var STICKY_BUTTON = 'product-sticky-bar [data-add-to-cart], product-sticky-bar [data-ref="submitButton"]';
  // The theme's own Add to cart in the product form (not "Add a gift message" etc.).
  var MAIN_BUTTON = '[data-add-to-cart], button[name="add"], button[type="submit"], input[type="submit"]';

  // ---------------------------------------------------------------------------
  // Pricing core (pure)
  // ---------------------------------------------------------------------------

  /** Highest tier whose qty fits n (tiers ascending), or null. */
  function tierFor(tiers, n) {
    var found = null;
    for (var i = 0; i < tiers.length; i += 1) {
      if (tiers[i].qty <= n) found = tiers[i];
    }
    return found;
  }

  /** One unit's saving at pct, half-up to the cent: the Function's rule. */
  function unitOff(cents, pct) {
    return Math.min(Math.round((cents * pct) / 100), cents);
  }

  function sum(list) {
    var total = 0;
    for (var i = 0; i < list.length; i += 1) total += list[i];
    return total;
  }

  /*
   * v2 greedy slices over pooled units ({cents}), per-unit share of each total
   * set on unit.off. Mirrors resolveDiscount in the Function; the spread across
   * units is proportional (Shopify allocates a fixed amount the same way).
   */
  function resolveSlices(tiers, units, rate) {
    var desc = tiers.slice().sort(function (a, b) {
      return b.qty - a.qty;
    });
    var pool = units.slice().sort(function (a, b) {
      return b.cents - a.cents;
    });
    var cursor = 0;
    var passes = 0;
    var discount = 0;
    var coveredCents = 0;
    var covered = [];

    while (cursor < pool.length && passes < MAX_TIER_PASSES) {
      var remaining = pool.length - cursor;
      var tier = null;
      for (var i = 0; i < desc.length; i += 1) {
        if (desc[i].qty <= remaining) {
          tier = desc[i];
          break;
        }
      }
      if (!tier) break;

      var slice = pool.slice(cursor, cursor + tier.qty);
      var sliceCents = 0;
      slice.forEach(function (unit) {
        sliceCents += unit.cents;
      });

      if (tier.kind === 'payFor') {
        slice.slice(tier.value).forEach(function (unit) {
          discount += unit.cents;
        });
      } else if (tier.kind === 'fixed') {
        discount += Math.min(Math.round(tier.value * rate), sliceCents);
      } else if (tier.kind === 'percent') {
        discount += Math.min(Math.round((sliceCents * tier.value) / 100), sliceCents);
      }

      covered = covered.concat(slice);
      coveredCents += sliceCents;
      cursor += tier.qty;
      passes += 1;
    }

    discount = Math.min(discount, coveredCents);

    var allocated = 0;
    var shares = covered.map(function (unit) {
      var exact = coveredCents ? (discount * unit.cents) / coveredCents : 0;
      var cents = Math.floor(exact);
      allocated += cents;
      return { unit: unit, cents: cents, rest: exact - cents };
    });
    shares
      .slice()
      .sort(function (a, b) {
        return b.rest - a.rest;
      })
      .slice(0, discount - allocated)
      .forEach(function (share) {
        share.cents += 1;
      });
    shares.forEach(function (share) {
      share.unit.off = share.cents;
    });
    return discount;
  }

  /*
   * Price the kits being added (newUnits, cents each) on top of the group units
   * already in the cart (cartUnits). Returns per-unit savings for both, so the
   * block can show slot prices, "Save $X" (rule 7: the multibuy discount on the
   * kits being added) and what the cart kits drop to (state G).
   */
  function quote(options, newUnits, cartUnits) {
    var tiers = options.tiers || [];
    var cart = cartUnits || [];
    var n = newUnits.length + cart.length;
    var newOff = [];
    var cartOff = [];
    var tier = tierFor(tiers, n);

    if (options.allUnits) {
      var pct = tier ? tier.value : 0;
      newOff = newUnits.map(function (cents) {
        return unitOff(cents, pct);
      });
      cartOff = cart.map(function (cents) {
        return unitOff(cents, pct);
      });
    } else {
      var pool = [];
      cart.forEach(function (cents) {
        pool.push({ cents: cents, isNew: false, off: 0 });
      });
      var newPool = newUnits.map(function (cents) {
        return { cents: cents, isNew: true, off: 0 };
      });
      pool = pool.concat(newPool);
      resolveSlices(tiers, pool, options.rate || 1);
      newOff = newPool.map(function (unit) {
        return unit.off;
      });
      cartOff = pool
        .filter(function (unit) {
          return !unit.isNew;
        })
        .map(function (unit) {
          return unit.off;
        });
    }

    var was = sum(newUnits);
    var save = sum(newOff);
    return {
      tier: tier,
      pct: options.allUnits && tier ? tier.value : 0,
      was: was,
      save: save,
      total: was - save,
      unitAfter: newUnits.map(function (cents, i) {
        return cents - newOff[i];
      }),
      cartSave: sum(cartOff),
      cartAfter: cart.map(function (cents, i) {
        return cents - cartOff[i];
      }),
    };
  }

  /*
   * Membership for a cart line from /cart.js (rule 8). Liquid decides it with
   * snippets/mix-match-eligible.liquid; the JS only sees product ids, so it
   * trusts the member list Liquid rendered (this kit, the swap list, the cart
   * members) plus the _mix_match property the block itself adds.
   */
  function isEligibleLine(line, context) {
    if (!line) return false;
    if (context.members && context.members[String(line.product_id)]) return true;
    var props = line.properties || {};
    return !!context.group && props._mix_match === context.group;
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  function fill(template, values) {
    return String(template || '').replace(/\[(\w+)\]/g, function (match, key) {
      return values[key] != null ? values[key] : match;
    });
  }

  function formatMoney(cents, format) {
    if (root.theme && root.theme.Currency && typeof root.theme.Currency.formatMoney === 'function') {
      return root.theme.Currency.formatMoney(cents, format);
    }
    var amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return String(format || '${{amount}}').replace(/\{\{\s*\w+\s*\}\}/, amount);
  }

  function currencyRate() {
    var rate = parseFloat(root.Shopify && root.Shopify.currency && root.Shopify.currency.rate);
    return rate > 0 ? rate : 1;
  }

  function pctText(value) {
    return String(Math.round(value * 100) / 100) + '%';
  }

  function setText(el, text) {
    if (!el) return;
    el.textContent = text || '';
    el.hidden = !text;
  }

  function allSame(list) {
    for (var i = 1; i < list.length; i += 1) if (list[i] !== list[0]) return false;
    return true;
  }

  function withParam(url, key, value) {
    return url + (url.indexOf('?') === -1 ? '?' : '&') + key + '=' + encodeURIComponent(value);
  }

  /*
   * Keep our text in a theme-owned node. The theme rewrites the Add to cart
   * label and the sticky price on every variant change, so each node is
   * watched: our text is put back, and the theme's latest text is remembered
   * for when we hand the node back (null).
   */
  function ownText(el, text) {
    if (!el) return;
    if (!el.mm3Watched) {
      el.mm3Watched = true;
      el.mm3Theme = el.textContent;
      new MutationObserver(function () {
        if (el.mm3Want == null) {
          el.mm3Theme = el.textContent;
          return;
        }
        if (el.textContent !== el.mm3Want) {
          el.mm3Theme = el.textContent;
          el.textContent = el.mm3Want;
        }
      }).observe(el, { childList: true, characterData: true, subtree: true });
    }
    el.mm3Want = text;
    var next = text == null ? el.mm3Theme : text;
    if (next != null && el.textContent !== next) el.textContent = next;
  }

  // ---------------------------------------------------------------------------
  // Saved selection (localStorage, 24h)
  //
  // The kits chosen in the box (2+ kits) are kept per group, so opening a
  // kit's full product page (new tab) or coming back later brings them back,
  // and the Mix & Match announcement bar can count them. Cleared when the
  // selection is added to cart, set back to 1 kit, or after 24 hours.
  //
  // It is the SAME bundle as the PLP "Build Your Own Bundle" tray
  // (plp-byob.js): kits added there show up here and the other way round. The
  // tray can hold 1 kit and its own add-ons ("extras"), which are kept.
  // ---------------------------------------------------------------------------

  var STORE_PREFIX = 'sculpd:mm3:selection:';
  var STORE_TTL = 24 * 60 * 60 * 1000;
  var SLOT_FIELDS = ['productId', 'handle', 'title', 'url', 'defaultOnly', 'variantId', 'variantTitle', 'price', 'image', 'available'];

  function readSelection(group) {
    if (!group) return null;
    try {
      var raw = root.localStorage.getItem(STORE_PREFIX + group);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (data && !Array.isArray(data.slots)) data.slots = [];
      // Kits and / or add-ons ("extras"): add-ons picked on their own are kept too.
      var fresh = data && (data.slots.length >= 1 || hasExtras(data)) && Date.now() - data.savedAt < STORE_TTL;
      if (!fresh) {
        root.localStorage.removeItem(STORE_PREFIX + group);
        return null;
      }
      return data;
    } catch (e) {
      return null;
    }
  }

  /*
   * Opens the cart slider after an add: UpCart's public API when the store
   * runs UpCart (refresh first so it shows the new lines), else nothing more
   * (the theme's own drawer opens on ajaxProduct:added).
   */
  function openCartDrawer() {
    try {
      var open = function () {
        if (typeof root.upcartOpenCart === 'function') root.upcartOpenCart();
      };
      if (typeof root.upcartRefreshCart === 'function') {
        var done = root.upcartRefreshCart();
        if (done && typeof done.then === 'function') done.then(open, open);
        else root.setTimeout(open, 0);
      } else {
        open();
      }
    } catch (e) {
      /* the cart still has the items; the theme drawer may open */
    }
  }
  root.SculpdOpenCart = root.SculpdOpenCart || openCartDrawer;

  function announceSelection(group, count) {
    try {
      document.dispatchEvent(new CustomEvent('mm3:selection', { detail: { group: group, count: count } }));
    } catch (e) {
      /* old browsers: the bar re-reads on its next cart update */
    }
  }

  /*
   * Add-ons ("extras") in the shared bundle: { "<addon product id>": { on: true,
   * vid: "<variant id>" } }, the PLP tray's format. Picked on a PDP or in the
   * PLP tray / sidebar, they follow the customer to every kit page and the
   * PLP, exactly like the kits.
   */
  function hasExtras(data) {
    var extras = data && data.extras;
    if (!extras || typeof extras !== 'object') return false;
    return Object.keys(extras).some(function (id) {
      return extras[id] && extras[id].on;
    });
  }

  function extrasSig(data) {
    var extras = (data && data.extras) || {};
    return Object.keys(extras)
      .filter(function (id) {
        return extras[id] && extras[id].on;
      })
      .sort()
      .map(function (id) {
        return id + ':' + String(extras[id].vid);
      })
      .join(',');
  }

  function storedRaw(group) {
    try {
      return JSON.parse(root.localStorage.getItem(STORE_PREFIX + group) || 'null');
    } catch (e) {
      return null;
    }
  }

  /** Writes slots + extras as they are; nothing left: removed. */
  function writeRaw(group, slots, extras) {
    try {
      if (!slots.length && !hasExtras({ extras: extras })) root.localStorage.removeItem(STORE_PREFIX + group);
      else root.localStorage.setItem(STORE_PREFIX + group, JSON.stringify({ v: 2, extras: extras, savedAt: Date.now(), qty: slots.length, slots: slots }));
    } catch (e) {
      /* private mode / storage full: the box still works, just not remembered */
    }
  }

  /** Returns the stored signature, so callers can skip identical writes. */
  function writeSelection(group, slots) {
    // Keep the add-ons (picked here or in the PLP tray).
    var extras = {};
    var before = storedRaw(group);
    if (before && before.extras && typeof before.extras === 'object') extras = before.extras;
    var data = {
      v: 2,
      extras: extras,
      savedAt: Date.now(),
      qty: slots.length,
      slots: slots.map(function (slot) {
        var out = {};
        SLOT_FIELDS.forEach(function (key) {
          out[key] = slot[key];
        });
        return out;
      }),
    };
    try {
      root.localStorage.setItem(STORE_PREFIX + group, JSON.stringify(data));
    } catch (e) {
      /* private mode / storage full: the box still works, just not remembered */
    }
    announceSelection(group, slots.length);
  }

  /** keepExtras: only the kits go (box back to 1 kit); the add-ons stay picked. */
  function clearSelection(group, keepExtras) {
    var before = keepExtras ? storedRaw(group) : null;
    if (before && hasExtras(before)) {
      writeRaw(group, [], before.extras);
    } else {
      try {
        root.localStorage.removeItem(STORE_PREFIX + group);
      } catch (e) {
        /* nothing to clear */
      }
    }
    announceSelection(group, 0);
  }

  /*
   * The kits in a list of slots, ignoring order: "productId:variantId" sorted.
   * Two pages showing the same bundle in a different order (each puts its own
   * kit first) must not keep rewriting it, or two open PDP tabs would bounce
   * the storage event between them forever.
   */
  function kitSet(slots) {
    return (slots || [])
      .map(function (slot) {
        return String(slot && slot.productId) + ':' + String(slot && slot.variantId);
      })
      .sort()
      .join(',');
  }

  // Only one block drives the theme's buttons (the first one initialised).
  var buttonOwner = null;

  // Quick view HTML, per product handle, shared by every block on the page.
  var quickViewCache = {};
  // Each kit's own "Swap kit: options to show", read from its own PDP (see loadKitFilter).
  var kitFilterCache = {};

  /*
   * iOS Safari: a scroll box whose content was just replaced can stay
   * partly unpainted (blank under the quick view's rating). A reflow and a
   * 1px scroll nudge, now and once images have laid out, force the repaint.
   */
  function repaintBody(box) {
    if (!box) return;
    // Only while the customer hasn't scrolled: writing scrollTop during a
    // (momentum) scroll stops it, which made the sheet feel unscrollable.
    var touched = false;
    var mark = function () {
      touched = true;
    };
    box.addEventListener('touchstart', mark, { once: true, passive: true });
    box.addEventListener('wheel', mark, { once: true, passive: true });
    function nudge() {
      if (touched || box.scrollTop !== 0) return;
      void box.offsetHeight;
      box.scrollTop = 1;
      box.scrollTop = 0;
    }
    nudge();
    root.setTimeout(nudge, 60);
    root.setTimeout(nudge, 400);
  }
  root.SculpdRepaintBody = repaintBody;

  /*
   * iOS (Safari AND Chrome: both are WebKit) leaves parts of the quick view's
   * scroll box white after its content changes: a new option picked (new
   * photo, prices, pills), a thumbnail tapped, an image finishing loading,
   * and after the customer has already scrolled (the scrollTop nudge above
   * never runs then). Re-painting the CONTENT for two frames fixes it without
   * touching the scroll position or putting a transform on the scroll box
   * (that swallowed taps on iOS).
   */
  function refreshPaint(box) {
    var content = box && box.firstElementChild;
    if (!content) return;
    var raf = root.requestAnimationFrame || function (fn) {
      return root.setTimeout(fn, 16);
    };
    content.style.opacity = '0.999';
    void content.offsetHeight;
    raf(function () {
      raf(function () {
        content.style.opacity = '';
      });
    });
  }
  root.SculpdRefreshPaint = refreshPaint;

  // The same filters, shared with the PLP tray (plp-byob.js) for 30 min:
  // every kit PDP writes its own; fetched ones are written too.
  var FILTER_PREFIX = 'sculpd:mm3:kitfilter:';
  var FILTER_TTL = 30 * 60 * 1000;
  function readFilterCache(handle) {
    try {
      var d = JSON.parse(root.localStorage.getItem(FILTER_PREFIX + handle) || 'null');
      if (d && Date.now() - d.at < FILTER_TTL && d.f && typeof d.f === 'object') return d.f;
    } catch (e) {
      /* no storage */
    }
    return null;
  }
  function writeFilterCache(handle, filter) {
    if (!handle) return;
    try {
      root.localStorage.setItem(FILTER_PREFIX + handle, JSON.stringify({ at: Date.now(), f: filter || {} }));
    } catch (e) {
      /* private mode */
    }
  }
  function handleFromUrl(url) {
    var m = String(url || '').match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  }

  // ---------------------------------------------------------------------------
  // Block
  // ---------------------------------------------------------------------------

  function init(widget) {
    if (!widget || widget.dataset.mm3Init === 'true') return;
    var configNode = widget.querySelector('script[data-mm3-config]');
    if (!configNode) return;
    var config;
    try {
      config = JSON.parse(configNode.textContent);
    } catch (e) {
      return;
    }
    if (!config || !Array.isArray(config.tiers) || !config.tiers.length) return;
    widget.dataset.mm3Init = 'true';

    var strings = config.strings || {};
    var tiers = config.tiers.slice().sort(function (a, b) {
      return a.qty - b.qty;
    });
    var pricing = { tiers: tiers, allUnits: !!config.allUnits, rate: currencyRate() };
    var maxSlots = config.maxSlots || 3;
    var rowTop = config.rowTop || Math.min(maxSlots, tiers[tiers.length - 1].qty);
    var addons = config.addons && Array.isArray(config.addons.items) && config.addons.items.length ? config.addons : null;
    var addonsById = {};
    if (addons) {
      addons.items.forEach(function (item) {
        addonsById[String(item.id)] = item;
      });
    }
    var product = config.product;
    var variantsById = {};
    product.variants.forEach(function (v) {
      variantsById[String(v.id)] = v;
    });
    var members = {};
    (config.memberIds || []).forEach(function (id) {
      members[String(id)] = true;
    });
    var eligibility = { members: members, group: config.group };

    var el = {
      tierInputs: Array.prototype.slice.call(widget.querySelectorAll('.mm3-tier__radio')),
      tierRows: Array.prototype.slice.call(widget.querySelectorAll('[data-mm3-tier]')),
      banner: widget.querySelector('[data-mm3-banner]'),
      bannerText: widget.querySelector('[data-mm3-banner-text]'),
      nudge: widget.querySelector('[data-mm3-nudge]'),
      nudgeAmount: widget.querySelector('[data-mm3-nudge-amount]'),
      panelWrap: widget.querySelector('[data-mm3-panel-wrap]'),
      panel: widget.querySelector('[data-mm3-panel]'),
      panelHeading: widget.querySelector('[data-mm3-panel-heading]'),
      slots: Array.prototype.slice.call(widget.querySelectorAll('[data-mm3-slot]')),
      after: widget.querySelector('[data-mm3-after]'),
      buy: widget.querySelector('[data-mm3-buy]'),
      saving: widget.querySelector('[data-mm3-saving]'),
      cartline: widget.querySelector('[data-mm3-cartline]'),
      error: widget.querySelector('[data-mm3-error]'),
      modal: widget.querySelector('[data-mm3-modal]'),
      addSlot: widget.querySelector('[data-mm3-add-slot]'),
      resume: widget.querySelector('[data-mm3-resume]'),
      resumeText: widget.querySelector('[data-mm3-resume-text]'),
      resumeContinue: widget.querySelector('[data-mm3-resume-continue]'),
      resumeView: widget.querySelector('[data-mm3-resume-view]'),
      addSlotLabel: widget.querySelector('[data-mm3-add-slot-label]'),
      addonRows: Array.prototype.slice.call(widget.querySelectorAll('[data-mm3-addon]')),
      addonsNote: widget.querySelector('[data-mm3-addons-note]'),
    };

    var state = {
      qty: 1,
      slots: [],
      kit1: null,
      cartUnits: [],
      busy: false,
      modalSlot: -1,
      modalTrigger: null,
      qv: null,
      // Add-ons: product id -> { variantId, on }
      addons: {},
      // Saved selection: written while true (the customer picked 2+ kits here,
      // or a saved selection was restored). A page that starts fresh at 1 kit
      // never touches what another page saved.
      persist: false,
      savedSig: '',
      resumable: null,
      // The saved bundle this box last showed (kitSet), to tell a real change
      // in another tab / page from our own write.
      seenSet: '',
      // The add-ons last read from / written to the saved bundle (extrasSig).
      seenExtras: '',
    };

    if (addons) {
      addons.items.forEach(function (item) {
        state.addons[String(item.id)] = { variantId: item.variantId, on: false };
      });
    }

    (config.cartLines || []).forEach(function (line) {
      for (var i = 0; i < line.q; i += 1) state.cartUnits.push(line.p);
    });

    if (!buttonOwner || !document.documentElement.contains(buttonOwner)) buttonOwner = widget;

    function money(cents) {
      return formatMoney(cents, config.moneyFormat);
    }

    // ---- Kit 1 follows the PDP's own picker ---------------------------------

    function productForm() {
      var forms = document.querySelectorAll(FORM_SELECTOR);
      for (var i = 0; i < forms.length; i += 1) {
        var form = forms[i];
        // Forms inside the block don't count, except the theme's own form once
        // it has been moved into the box (setting "Add to cart inside the box").
        if (el.modal && el.modal.contains(form)) continue;
        if (widget.contains(form) && !(el.buy && el.buy.contains(form))) continue;
        var idInput = form.querySelector('[name="id"]');
        if (idInput && variantsById[String(idInput.value)]) return form;
      }
      return null;
    }

    function kitFromVariant(variant, available) {
      return {
        productId: product.id,
        handle: product.handle,
        title: product.title,
        url: product.url,
        defaultOnly: product.defaultOnly,
        variantId: variant.id,
        variantTitle: variant.title,
        price: variant.price,
        image: variant.image,
        available: available != null ? available : variant.available,
        swapped: false,
      };
    }

    function initialKit1() {
      var form = productForm();
      var idInput = form && form.querySelector('[name="id"]');
      var variant = (idInput && variantsById[String(idInput.value)]) || variantsById[String(config.variantId)];
      return kitFromVariant(variant || product.variants[0]);
    }

    function setKit1(kit) {
      state.kit1 = kit;
      state.slots = state.slots.map(function (slot, i) {
        return i === 0 || !slot.swapped ? copy(kit) : slot;
      });
    }

    function copy(kit) {
      var out = {};
      for (var key in kit) out[key] = kit[key];
      out.swapped = false;
      return out;
    }

    // Down a tier: drop from the end, un-swapped kits first, so the customer's
    // choices survive. Up a tier: add copies of Kit 1.
    function resize(slots, qty) {
      var next = slots.slice();
      while (next.length < qty) next.push(copy(state.kit1));
      while (next.length > qty) {
        var drop = -1;
        for (var i = next.length - 1; i >= 1; i -= 1) {
          if (!next[i].swapped) {
            drop = i;
            break;
          }
        }
        next.splice(drop === -1 ? next.length - 1 : drop, 1);
      }
      return next;
    }

    function prices(slots) {
      return slots.map(function (slot) {
        return slot.price;
      });
    }

    function quoteSlots(slots) {
      return quote(pricing, prices(slots), state.cartUnits);
    }

    // ---- Add-ons -------------------------------------------------------------

    function addonVariant(id) {
      var item = addonsById[id];
      var pick = state.addons[id];
      if (!item || !pick) return null;
      for (var i = 0; i < item.variants.length; i += 1) {
        if (String(item.variants[i].id) === String(pick.variantId)) return item.variants[i];
      }
      return item.variants[0] || null;
    }

    function addonsUnlocked() {
      return !!addons && state.cartUnits.length + state.qty >= addons.minKits;
    }

    /** The picked add-ons: { count, was, save, total, items: [{variantId}] }. */
    function quoteAddons() {
      var out = { count: 0, was: 0, save: 0, total: 0, items: [] };
      if (!addons) return out;
      var pct = addonsUnlocked() ? addons.pct : 0;
      Object.keys(state.addons).forEach(function (id) {
        if (!state.addons[id].on) return;
        var variant = addonVariant(id);
        if (!variant) return;
        var off = pct ? unitOff(variant.price, pct) : 0;
        out.count += 1;
        out.was += variant.price;
        out.save += off;
        out.items.push({ variantId: variant.id });
      });
      out.total = out.was - out.save;
      return out;
    }

    function renderAddons() {
      if (!addons) return;
      var unlocked = addonsUnlocked();
      var pctLabel = pctText(addons.pct);
      if (el.addonsNote) {
        el.addonsNote.textContent = fill(unlocked ? strings.addonUnlocked : strings.addonLocked, { pct: pctLabel, min: addons.minKits });
        el.addonsNote.classList.toggle('is-unlocked', unlocked);
      }
      el.addonRows.forEach(function (li) {
        var id = li.getAttribute('data-mm3-addon');
        var variant = addonVariant(id);
        var pick = state.addons[id];
        if (!variant || !pick) return;
        var off = unlocked ? unitOff(variant.price, addons.pct) : 0;
        setText(li.querySelector('[data-mm3-addon-was]'), off > 0 ? money(variant.price) : '');
        var now = li.querySelector('[data-mm3-addon-now]');
        if (now) now.textContent = money(variant.price - off);
        li.classList.toggle('is-added', pick.on);
        var select = li.querySelector('[data-mm3-addon-variant]');
        if (select && String(select.value) !== String(pick.variantId)) {
          for (var o = 0; o < select.options.length; o += 1) {
            if (String(select.options[o].value) === String(pick.variantId)) {
              select.value = select.options[o].value;
              break;
            }
          }
        }
        var toggle = li.querySelector('[data-mm3-addon-toggle]');
        if (toggle) toggle.setAttribute('aria-pressed', pick.on ? 'true' : 'false');
        var label = li.querySelector('[data-mm3-addon-toggle-label]');
        if (label) label.textContent = pick.on ? strings.addonAdded : strings.addonAdd;
      });
    }

    // ---- Render --------------------------------------------------------------

    function render() {
      var qty = state.qty;
      var slots = state.slots;
      var current = quoteSlots(slots);
      var cartCount = state.cartUnits.length;

      // Tier rows: the selected row prices the real slots; the others estimate
      // from them, filling extra slots with this kit (rule 3). The top row
      // ("4+ kits") stays selected for every count from rowTop up to maxSlots.
      var selectedRow = Math.min(qty, rowTop);
      el.tierRows.forEach(function (row) {
        var rowQty = parseInt(row.getAttribute('data-mm3-tier'), 10);
        var selected = rowQty === selectedRow;
        var estimate = selected ? slots : resize(slots, rowQty);
        var q = selected ? current : quoteSlots(estimate);
        row.classList.toggle('is-selected', selected);
        var input = row.querySelector('.mm3-tier__radio');
        if (input) input.checked = selected;

        var sub;
        if (rowQty === 1) {
          sub = q.save > 0 && q.pct ? fill(strings.withCartKit, { pct: pctText(q.pct) }) : strings.oneKit;
        } else if (q.save > 0 && allSame(prices(estimate))) {
          sub = fill(strings.each, { amount: money(q.unitAfter[0]) });
        } else if (q.pct) {
          sub = fill(strings.everyKit, { pct: pctText(q.pct) });
        } else {
          sub = q.tier ? q.tier.off : '';
        }
        var subEl = row.querySelector('[data-mm3-tier-sub]');
        if (subEl) subEl.textContent = sub;
        setText(row.querySelector('[data-mm3-tier-was]'), q.save > 0 ? money(q.was) : '');
        var totalEl = row.querySelector('[data-mm3-tier-total]');
        if (totalEl) totalEl.textContent = money(q.total);
        setText(row.querySelector('[data-mm3-tier-save]'), q.save > 0 ? fill(strings.save, { amount: money(q.save) }) : '');
        if (input) {
          var label = row.querySelector('.mm3-tier__label');
          input.setAttribute(
            'aria-label',
            (label ? label.textContent : rowQty) + ', ' + money(q.total) + (q.save > 0 ? ', ' + fill(strings.save, { amount: money(q.save) }) : '')
          );
        }
      });

      // State G banner.
      if (el.banner) {
        el.banner.hidden = cartCount === 0;
        if (cartCount) {
          var countText = cartCount === 1 ? strings.kitOne : fill(strings.kitMany, { count: cartCount });
          var unlocked = tierFor(tiers, cartCount + 1);
          el.bannerText.textContent = pricing.allUnits && unlocked
            ? fill(strings.bannerUnlocked, { count: countText, pct: pctText(unlocked.value) })
            : fill(strings.bannerPlain, { count: countText });
        }
      }

      // State A nudge: "Add a 2nd kit, any kit, and save $17.68".
      var twoRow = widget.querySelector('[data-mm3-tier="2"]');
      var nudgeQuote = qty === 1 && current.save === 0 && twoRow ? quoteSlots(resize(slots, 2)) : null;
      if (el.nudge) {
        el.nudge.hidden = !(nudgeQuote && nudgeQuote.save > 0);
        if (nudgeQuote) el.nudgeAmount.textContent = money(nudgeQuote.save);
      }

      // Slots panel (hidden at 1 kit; animates open).
      var open = qty > 1;
      el.panelWrap.classList.toggle('is-open', open);
      if (open) el.panel.removeAttribute('inert');
      else el.panel.setAttribute('inert', '');
      el.panelHeading.textContent = fill(strings.panelHeading, { count: qty });
      var showPrices = !allSame(prices(slots));
      var anySoldOut = false;
      el.slots.forEach(function (li, i) {
        var slot = slots[i];
        li.hidden = !slot || i >= qty;
        if (!slot) return;
        var img = li.querySelector('.mm3-slot__img');
        if (img && slot.image && img.getAttribute('src') !== slot.image) {
          img.setAttribute('src', slot.image);
          img.removeAttribute('srcset');
        }
        li.querySelector('[data-mm3-slot-title]').textContent = slot.title;
        setText(li.querySelector('[data-mm3-slot-variant]'), slot.defaultOnly ? '' : slot.variantTitle);
        li.querySelector('[data-mm3-slot-swapped]').hidden = !slot.swapped;
        li.classList.toggle('is-swapped', slot.swapped);
        var priceEl = li.querySelector('[data-mm3-slot-price]');
        priceEl.hidden = !showPrices;
        if (showPrices) {
          setText(li.querySelector('[data-mm3-slot-was]'), current.unitAfter[i] < slot.price ? money(slot.price) : '');
          li.querySelector('[data-mm3-slot-now]').textContent = money(current.unitAfter[i]);
        }
        li.querySelector('[data-mm3-slot-soldout]').hidden = slot.available;
        if (!slot.available && i < qty) anySoldOut = true;
        var swapLabel = li.querySelector('[data-mm3-swap-label]');
        if (swapLabel) {
          swapLabel.innerHTML = slot.swapped
            ? strings.change
            : '<span aria-hidden="true">⇄</span> ' + strings.swap;
          var swapButton = li.querySelector('[data-mm3-swap]');
          swapButton.setAttribute('aria-label', (slot.swapped ? strings.change : strings.swap) + ': ' + li.querySelector('.mm3-slot__eyebrow').firstChild.textContent.trim());
        }
      });
      state.blocked = open && anySoldOut;

      // Kit slot control: extra kits on the top row, up to maxSlots.
      el.slots.forEach(function (li, i) {
        var remove = li.querySelector('[data-mm3-remove]');
        if (remove) remove.hidden = !(i >= 1 && i < qty);
      });
      if (el.addSlot) {
        var canAdd = open && qty < maxSlots;
        el.addSlot.hidden = !canAdd;
        if (canAdd && el.addSlotLabel) {
          // "· 15% off every kit" while the next kit unlocks a higher %, then
          // "(2 more)" on the top row.
          var next = pricing.allUnits ? tierFor(tiers, state.cartUnits.length + qty + 1) : null;
          var left = maxSlots - qty;
          var label = strings.addSlot;
          if (next && next.value > current.pct && strings.addSlotSave) {
            label = fill(strings.addSlotSave, { pct: pctText(next.value) });
          } else if (left > 1) {
            label = fill(strings.addSlotLeft, { left: left });
          }
          el.addSlotLabel.textContent = label;
        }
      }

      renderAddons();
      var extras = quoteAddons();
      var saveAll = current.save + extras.save;

      // Saving line under the button.
      // The amount includes the add-ons' saving.
      var savingText = '';
      if (qty > 1 && current.save > 0) {
        if (!pricing.allUnits) {
          savingText = fill(strings.savingTier, { off: current.tier ? current.tier.off : '', amount: money(saveAll) });
        } else if (allSame(prices(slots))) {
          savingText = fill(strings.savingSame, { amount: money(saveAll) });
        } else {
          var baseline = quote(pricing, slots.map(function () { return state.kit1.price; }), state.cartUnits);
          savingText = fill(current.save < baseline.save ? strings.savingCheaper : strings.savingMixed, {
            pct: pctText(current.pct),
            amount: money(saveAll),
          });
        }
      } else if (extras.save > 0) {
        savingText = fill(strings.savingSame, { amount: money(saveAll) });
      }
      setText(el.saving, savingText);

      // State G: what the kits already in the cart drop to.
      var cartText = '';
      if (cartCount && current.cartSave > 0) {
        cartText = cartCount === 1
          ? fill(strings.cartKitOne, { amount: money(current.cartAfter[0]) })
          : fill(strings.cartKitMany, { count: cartCount, amount: money(current.cartSave) });
      }
      setText(el.cartline, cartText);

      renderButtons(current, extras);
      renderResume();
      persistSelection();
    }

    function persistSelection() {
      if (!state.persist) return;
      if (state.qty < 2) {
        state.persist = false;
        state.savedSig = '';
        state.seenSet = '';
        clearSelection(config.group, true);
        return;
      }
      var live = state.slots.slice(0, state.qty);
      var sig = JSON.stringify(
        live.map(function (slot) {
          return [slot.productId, slot.variantId, slot.price, slot.available];
        })
      );
      if (sig === state.savedSig) return;
      state.savedSig = sig;
      // Same kits as the saved bundle (only the order differs: this page shows
      // its own kit first): leave it, so the PLP keeps its order and other
      // tabs aren't told about a change that isn't one.
      var saved = readSelection(config.group);
      var set = kitSet(live);
      if (saved && kitSet(saved.slots) === set) {
        state.seenSet = set;
        return;
      }
      state.seenSet = set;
      writeSelection(config.group, live);
    }

    /** A saved slot back into a kit; it follows Kit 1 only if it is Kit 1's exact variant. */
    function restoredKit(saved) {
      var kit = {};
      SLOT_FIELDS.forEach(function (key) {
        kit[key] = saved[key];
      });
      kit.available = saved.available !== false;
      kit.swapped = !(String(saved.productId) === String(state.kit1.productId) && String(saved.variantId) === String(state.kit1.variantId));
      members[String(saved.productId)] = true;
      return kit;
    }

    function applySaved(kits, qty) {
      state.slots = [copy(state.kit1)].concat(kits.map(restoredKit)).slice(0, maxSlots);
      state.qty = Math.max(1, Math.min(qty, state.slots.length, maxSlots));
      state.persist = state.qty >= 2;
      state.resumable = null;
    }

    /*
     * On load: a saved selection that includes this product reopens with this
     * kit as Kit 1 and the others after it. One that doesn't is kept, and the
     * box offers "You have a 2-kit selection saved · Continue" (this kit is
     * then added as Kit 1).
     */
    function indexOfThisKit(saved) {
      for (var i = 0; i < saved.slots.length; i += 1) {
        if (String(saved.slots[i].productId) === String(product.id)) return i;
      }
      return -1;
    }

    /*
     * The offer to show for a saved bundle this box isn't showing:
     *   fits: "Continue" (this kit + the bundle fits in the box's slots)
     *   too big for the box (the PLP tray allows more kits): "View bundle",
     *   a link to the collection page, so no kit is ever dropped.
     */
    function offerFor(saved) {
      if (!saved) return null;
      var here = indexOfThisKit(saved) !== -1;
      var fits = (here ? saved.slots.length : saved.slots.length + 1) <= maxSlots;
      return { saved: saved, fits: fits, here: here };
    }

    // ---- Add-ons in the shared bundle -------------------------------------

    /** The saved add-ons onto this box's add-on rows (on / off + option). */
    function applyExtras(saved) {
      var extras = (saved && saved.extras) || {};
      Object.keys(state.addons).forEach(function (id) {
        var pick = state.addons[id];
        var e = extras[id];
        var item = addonsById[id];
        pick.on = !!(e && e.on);
        if (pick.on && item && e.vid != null) {
          for (var i = 0; i < item.variants.length; i += 1) {
            if (String(item.variants[i].id) === String(e.vid)) {
              pick.variantId = item.variants[i].id;
              break;
            }
          }
        }
      });
      state.seenExtras = extrasSig(saved);
    }

    /** This box's add-on picks into the saved bundle (kits untouched). */
    function saveExtras() {
      if (!addons) return;
      var before = storedRaw(config.group);
      var fresh = before && Date.now() - before.savedAt < STORE_TTL;
      var slots = fresh && Array.isArray(before.slots) ? before.slots : [];
      var extras = {};
      // Add-ons this page doesn't list (another page's) are kept as they are.
      var old = (fresh && before.extras) || {};
      Object.keys(old).forEach(function (id) {
        if (!addonsById[id] && old[id] && old[id].on) extras[id] = old[id];
      });
      Object.keys(state.addons).forEach(function (id) {
        var pick = state.addons[id];
        if (pick.on) extras[id] = { on: true, vid: String(pick.variantId) };
      });
      var sig = extrasSig({ extras: extras });
      if (sig === extrasSig(before) && fresh) {
        state.seenExtras = sig;
        return;
      }
      state.seenExtras = sig;
      writeRaw(config.group, slots, extras);
      announceSelection(config.group, slots.length);
    }

    /*
     * Shows the saved bundle (from this box on another kit's page, or from the
     * PLP tray / sidebar "Your kits") in "Your N kits":
     *   - it has this kit: this kit as Kit 1, the others after it, same count;
     *   - it doesn't: this kit as Kit 1 and every saved kit after it. Nothing
     *     is written until the customer changes something here, so just
     *     opening a kit page never adds that kit to the bundle; setting the
     *     box back to 1 kit keeps the bundle and offers "Continue".
     *   - too many kits for the box's slots: "View bundle" (nothing dropped).
     */
    function restoreSelection(saved) {
      if (saved === undefined) saved = readSelection(config.group);
      state.seenSet = saved ? kitSet(saved.slots) : '';
      applyExtras(saved);
      // Only add-ons saved: they're picked above; no kits to show or offer.
      if (!saved || !saved.slots.length) return;
      var offer = offerFor(saved);
      if (!offer.fits) {
        state.resumable = offer;
        return;
      }
      var match = indexOfThisKit(saved);
      if (match !== -1) {
        var others = saved.slots.slice(0, match).concat(saved.slots.slice(match + 1));
        applySaved(others, saved.slots.length);
        return;
      }
      applySaved(saved.slots, saved.slots.length + 1);
      state.persist = false;
      state.resumable = offer;
    }

    /*
     * The saved bundle changed somewhere else (PLP or another kit page in
     * another tab, or this page came back from the back / forward cache):
     * show it. Our own writes don't count (same kits).
     */
    function syncSelection() {
      if (state.busy) return;
      var saved = readSelection(config.group);
      var set = saved ? kitSet(saved.slots) : '';
      if (set === state.seenSet) {
        // Same kits; the add-ons may have changed (PLP tray, another PDP).
        if (extrasSig(saved) === state.seenExtras) return;
        applyExtras(saved);
        render();
        return;
      }
      // The kit being swapped may no longer exist: close the list first.
      if (modal && !modal.hidden) closeModal();
      state.qty = 1;
      state.slots = [copy(state.kit1)];
      state.persist = false;
      state.savedSig = '';
      state.resumable = null;
      restoreSelection(saved);
      showError('');
      render();
    }

    function renderResume() {
      if (!el.resume) return;
      var offer = state.resumable;
      var show = !!offer && offer.saved.slots.length > 0 && state.qty === 1 && !(offer.here && offer.saved.slots.length === 1);
      el.resume.hidden = !show;
      if (!show) return;
      var count = offer.saved.slots.length;
      if (el.resumeText) el.resumeText.textContent = fill(offer.fits ? strings.resume : strings.resumeBig || strings.resume, { count: count });
      if (el.resumeContinue) el.resumeContinue.hidden = !offer.fits;
      if (el.resumeView) el.resumeView.hidden = offer.fits;
    }

    function resumeSelection() {
      var offer = state.resumable;
      if (!offer || !offer.fits) return;
      var saved = offer.saved;
      var match = indexOfThisKit(saved);
      var others = match === -1 ? saved.slots : saved.slots.slice(0, match).concat(saved.slots.slice(match + 1));
      applySaved(others, others.length + 1);
      showError('');
      render();
    }

    // Block settings "Add to cart button" colours: CSS variables on the
    // theme's button, applied by mix-match-v3.css only for the colours set.
    var ATC_VARS = { bg: '--mm3-atc-bg', text: '--mm3-atc-text', bgHover: '--mm3-atc-bg-hover', textHover: '--mm3-atc-text-hover' };
    function colourButton(button) {
      if (!button || button.dataset.mm3Colours === 'true') return;
      var colours = config.atcColors || {};
      var any = false;
      Object.keys(ATC_VARS).forEach(function (key) {
        if (colours[key]) {
          button.style.setProperty(ATC_VARS[key], colours[key]);
          button.classList.add('mm3-atc--' + key);
          any = true;
        }
      });
      if (any) button.classList.add('mm3-atc');
      button.dataset.mm3Colours = 'true';
    }

    function renderButtons(current, extras) {
      extras = extras || quoteAddons();
      if (buttonOwner !== widget) return;
      var form = productForm();
      var mainText = form && form.querySelector('[data-add-to-cart-text]');
      var mainButton = form && form.querySelector('[data-add-to-cart], .add-to-cart, [type="submit"]');
      colourButton(mainButton);
      var qty = state.qty;
      // Leave the theme's own state ("Sold Out") alone when Kit 1 itself can't be bought.
      var ours = state.kit1.available && !state.blocked;

      var grand = current.total + extras.total;
      var mainLabel;
      var stickyLabel;
      if (extras.count) {
        var parts = {
          kits: qty === 1 ? strings.kitOne : fill(strings.kitMany, { count: qty }),
          extras: extras.count === 1 ? strings.extraOne : fill(strings.extraMany, { count: extras.count }),
          amount: money(grand),
        };
        mainLabel = fill(strings.addExtras, parts);
        stickyLabel = fill(strings.addStickyExtras, parts);
      } else {
        mainLabel = fill(qty === 1 ? strings.addOne : strings.addMany, { count: qty, amount: money(grand) });
        stickyLabel = fill(qty === 1 ? strings.addStickyOne : strings.addStickyMany, { count: qty });
      }

      ownText(mainText, ours ? mainLabel : null);
      Array.prototype.forEach.call(document.querySelectorAll(STICKY_TEXT), function (node) {
        ownText(node, ours ? stickyLabel : null);
      });
      Array.prototype.forEach.call(document.querySelectorAll(STICKY_PRICE), function (node) {
        ownText(node, ours ? money(grand) : null);
      });

      // A sold-out kit in a slot blocks the add (2+ kits only; 1 kit is the theme's).
      var buttons = Array.prototype.slice.call(document.querySelectorAll(STICKY_BUTTON));
      if (mainButton) buttons.push(mainButton);
      buttons.forEach(function (button) {
        if (state.blocked) {
          if (!button.disabled) button.dataset.mm3Disabled = 'true';
          button.disabled = true;
        } else if (button.dataset.mm3Disabled === 'true') {
          delete button.dataset.mm3Disabled;
          button.disabled = !state.kit1.available;
        }
      });
    }

    // The theme's Buy buttons block into the bottom of the box, as in the
    // design. Its form and listeners move with it, untouched.
    //
    // The WHOLE block must move: the PDP blocks sit in a CSS grid, where
    // margins don't collapse, so an emptied wrapper left behind would add its
    // 28px margin to the gap under the box (seen on staging, where Buy buttons
    // is two nested plain .product-block divs). So: the form's ancestor that
    // is a direct child of the same blocks container as this block.
    function buyButtonsBlock(form) {
      var ownBlock = widget.closest ? widget.closest('.product-block') : null;
      var blocks = ownBlock && ownBlock.parentElement;
      if (blocks) {
        var node = form;
        while (node && node.parentElement !== blocks) node = node.parentElement;
        if (node && node !== ownBlock) return node;
      }
      // Unknown layout: the outermost .product-block around the form that
      // doesn't hold this block, else the form itself.
      var found = form;
      var up = form.parentElement;
      while (up && up !== document.body) {
        if (up.classList && up.classList.contains('product-block') && !up.contains(widget)) found = up;
        if (up.contains(widget)) break;
        up = up.parentElement;
      }
      return found;
    }

    function placeBuyButtons() {
      if (!config.atcInside || !el.buy) return;
      var form = productForm();
      if (!form || el.buy.contains(form)) return;
      var container = buyButtonsBlock(form);
      if (!container || container.contains(widget)) return;
      el.buy.appendChild(container);
      el.buy.hidden = false;
    }

    // Under the theme's Add to cart, where the design puts it.
    function placeAfterButton() {
      var form = productForm();
      var button = form && form.querySelector('[data-add-to-cart], .add-to-cart');
      if (button && button.parentNode && el.after && button.nextSibling !== el.after) {
        button.parentNode.insertBefore(el.after, button.nextSibling);
      }
    }

    function setQty(qty) {
      qty = Math.max(1, Math.min(maxSlots, qty));
      state.qty = qty;
      state.slots = resize(state.slots, qty);
      if (qty >= 2) {
        state.persist = true;
        state.resumable = null;
      }
      showError('');
      render();
    }

    function removeSlot(i) {
      if (i < 1 || i >= state.qty) return;
      state.slots.splice(i, 1);
      state.qty -= 1;
      // The customer edited the bundle: save it (back to 1 kit clears it).
      state.persist = true;
      state.resumable = null;
      showError('');
      render();
    }

    // ---- Kit 1: "↑ Change options above" -> the page's options picker -------

    /** The PDP's own options picker (not one inside this box or its quick view). */
    function optionsPicker() {
      var ours = function (node) {
        return widget.contains(node) || (el.modal && el.modal.contains(node));
      };
      var wrappers = document.querySelectorAll('.variant-wrapper, [data-index^="option"], variant-selects, variant-radios');
      for (var i = 0; i < wrappers.length; i += 1) {
        var node = wrappers[i];
        if (ours(node) || node.closest('[hidden]')) continue;
        if (!(node.offsetWidth || node.offsetHeight || node.getClientRects().length)) continue;
        // The whole picker block (every option), when the theme wraps it in one.
        var block = node.closest('.product-block');
        return block && !ours(block) ? block : node;
      }
      return null;
    }

    function scrollToOptions() {
      var target = optionsPicker();
      if (!target) return;
      var reduce = root.matchMedia && root.matchMedia('(prefers-reduced-motion: reduce)').matches;
      // A quarter down the screen: clear of the sticky header / announcement bar.
      var top = target.getBoundingClientRect().top + (root.pageYOffset || document.documentElement.scrollTop) - root.innerHeight * 0.25;
      try {
        root.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'auto' : 'smooth' });
      } catch (e) {
        root.scrollTo(0, Math.max(0, top));
      }
      target.classList.remove('mm3-picker-flash');
      void target.offsetWidth; // restart the highlight
      target.classList.add('mm3-picker-flash');
      root.setTimeout(function () {
        target.classList.remove('mm3-picker-flash');
      }, 1700);
      // Keyboard users land on the picker too (no second jump).
      var control = target.querySelector('input:checked, select, input, button');
      if (control && control.focus) {
        try {
          control.focus({ preventScroll: true });
        } catch (e) {
          /* old browsers: leave focus */
        }
      }
    }

    function showError(text) {
      setText(el.error, text);
    }

    /** The box handles the add itself: 2+ kits, or any add-on picked. */
    function ownsAdd() {
      return state.qty >= 2 || quoteAddons().count > 0;
    }

    // ---- Add to cart ---------------------------------------------------------

    function addSlots(trigger) {
      if (state.busy || state.blocked) return;
      var byVariant = {};
      var order = [];
      state.slots.slice(0, state.qty).forEach(function (slot) {
        var key = String(slot.variantId);
        if (!byVariant[key]) {
          byVariant[key] = 0;
          order.push(key);
        }
        byVariant[key] += 1;
      });
      var items = order.map(function (id) {
        // Hidden (underscore) property: marks the line in the cart and in
        // orders. Qualification itself comes from the product metafield.
        return { id: parseInt(id, 10), quantity: byVariant[id], properties: { _mix_match: config.group } };
      });
      quoteAddons().items.forEach(function (extra) {
        // Hidden property, as for the kits; the 25% comes from the product
        // metafield ("mixMatchAddon"), not from this.
        items.push({ id: parseInt(extra.variantId, 10), quantity: 1, properties: { _mix_match_addon: config.group } });
      });
      var addedUnits = prices(state.slots.slice(0, state.qty));

      state.busy = true;
      showError('');
      if (trigger) trigger.classList.add('btn--loading');

      var url = (root.theme && root.theme.routes && root.theme.routes.cartAdd) || '/cart/add.js';
      fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items }),
      })
        .then(function (response) {
          return response.json().then(function (data) {
            return { ok: response.ok, data: data };
          });
        })
        .then(function (result) {
          if (!result.ok || result.data.status) {
            // 422 etc.: keep the selection so the customer can fix it.
            showError((result.data && result.data.description) || strings.error);
            return;
          }
          state.cartUnits = state.cartUnits.concat(addedUnits);
          state.slots.slice(0, state.qty).forEach(function (slot) {
            members[String(slot.productId)] = true;
          });
          // Done: back to "1 kit", the kits added now count from the cart, and
          // the saved selection is used up.
          clearSelection(config.group);
          state.persist = false;
          state.savedSig = '';
          state.seenSet = '';
          state.seenExtras = '';
          state.resumable = null;
          state.qty = 1;
          state.slots = [copy(state.kit1)];
          Object.keys(state.addons).forEach(function (id) {
            state.addons[id].on = false;
          });
          document.dispatchEvent(
            new CustomEvent('ajaxProduct:added', { detail: { product: result.data, addToCartBtn: trigger } })
          );
          openCartDrawer();
        })
        .catch(function () {
          showError(strings.error);
        })
        .then(function () {
          state.busy = false;
          if (trigger) trigger.classList.remove('btn--loading');
          render();
        });
    }

    function isProductForm(form) {
      return !!form && form === productForm();
    }

    if (buttonOwner === widget) {
      // With 2+ kits (or add-ons picked) the box adds the slots and add-ons
      // itself; 1 kit and no add-ons: untouched.
      //
      // Cart apps (UpCart) take the Add to cart CLICK and add the form
      // themselves, so the form's submit never happens. So the click on the
      // main button or the sticky bar is taken here too, on WINDOW in the
      // capture phase: that runs before any listener on document, the form or
      // the button, whoever registered first. Submit (Enter key, or a theme
      // with no click handler) is covered the same way.
      var takeOver = function (event, trigger) {
        event.preventDefault();
        event.stopImmediatePropagation();
        event.stopPropagation();
        addSlots(trigger);
      };
      root.addEventListener(
        'click',
        function (event) {
          if (buttonOwner !== widget || !ownsAdd() || !event.target.closest) return;
          var sticky = event.target.closest(STICKY_BUTTON);
          if (sticky) {
            takeOver(event, sticky);
            return;
          }
          var button = event.target.closest(MAIN_BUTTON);
          if (!button || button.disabled) return;
          var form = button.form || button.closest('form');
          if (!isProductForm(form)) return;
          takeOver(event, button);
        },
        true
      );
      // 1 kit, the theme (or UpCart) adds it: a saved bundle that is just this
      // kit (e.g. added on a collection page) is then in the cart, so it is
      // used up, or the bar would count it twice.
      root.addEventListener(
        'click',
        function (event) {
          if (buttonOwner !== widget || ownsAdd() || !event.target.closest) return;
          var button = event.target.closest(MAIN_BUTTON);
          if (!button || button.disabled) return;
          var form = button.form || button.closest('form');
          if (!isProductForm(form)) return;
          var saved = readSelection(config.group);
          var onlyThis = saved && saved.slots.length > 0 && saved.slots.every(function (slot) {
            return String(slot.productId) === String(product.id);
          });
          if (onlyThis) {
            state.seenSet = '';
            clearSelection(config.group, true);
            state.resumable = null;
          }
        },
        true
      );
      root.addEventListener(
        'submit',
        function (event) {
          if (buttonOwner !== widget || !ownsAdd() || !isProductForm(event.target)) return;
          takeOver(event, event.submitter || event.target.querySelector('[data-add-to-cart], [type="submit"]'));
        },
        true
      );
    }

    // ---- Events ----------------------------------------------------------------

    widget.addEventListener('change', function (event) {
      var radio = event.target.closest('.mm3-tier__radio');
      if (radio) {
        setQty(parseInt(radio.value, 10));
        return;
      }
      var variantSelect = event.target.closest('[data-mm3-addon-variant]');
      if (variantSelect) {
        var addonRow = variantSelect.closest('[data-mm3-addon]');
        var pick = addonRow && state.addons[addonRow.getAttribute('data-mm3-addon')];
        if (pick) {
          pick.variantId = parseInt(variantSelect.value, 10);
          render();
          if (pick.on) saveExtras();
        }
      }
    });

    widget.addEventListener('click', function (event) {
      if (event.target.closest('[data-mm3-edit-options]')) {
        scrollToOptions();
        return;
      }
      if (event.target.closest('[data-mm3-resume-continue]')) {
        resumeSelection();
        return;
      }
      var addSlotButton = event.target.closest('[data-mm3-add-slot]');
      if (addSlotButton) {
        if (state.qty >= maxSlots) return;
        setQty(state.qty + 1);
        // Straight to "Choose kit 3 of 3"; closing it keeps the copy of Kit 1.
        openModal(state.qty - 1, addSlotButton);
        return;
      }
      var remove = event.target.closest('[data-mm3-remove]');
      if (remove) {
        removeSlot(parseInt(remove.getAttribute('data-mm3-remove'), 10));
        return;
      }
      var addonToggle = event.target.closest('[data-mm3-addon-toggle]');
      if (addonToggle) {
        var addonLi = addonToggle.closest('[data-mm3-addon]');
        var addonPick = addonLi && state.addons[addonLi.getAttribute('data-mm3-addon')];
        if (addonPick) {
          addonPick.on = !addonPick.on;
          showError('');
          render();
          saveExtras();
        }
        return;
      }
      var swap = event.target.closest('[data-mm3-swap]');
      if (swap) {
        openModal(parseInt(swap.getAttribute('data-mm3-swap'), 10), swap);
        return;
      }
      var viewCart = event.target.closest('[data-mm3-view-cart]');
      if (viewCart) {
        var trigger = document.getElementById('HeaderCartTrigger');
        if (trigger && trigger !== viewCart) {
          event.preventDefault();
          trigger.click();
        }
      }
    });

    // Kit 1 and every un-swapped slot follow the PDP's variant picker.
    document.addEventListener('variant:change', function (event) {
      if (!document.documentElement.contains(widget)) return;
      var variant = event.detail && event.detail.variant;
      var known = variant && variantsById[String(variant.id)];
      if (variant && !known) return; // another product's picker (quick add etc.)
      if (known) {
        // The event's availability is live; the rendered data is from page load.
        setKit1(kitFromVariant(known, variant.available != null ? !!variant.available : known.available));
      } else {
        // A combination that doesn't exist: Kit 1 can't be bought as picked.
        var kit = copy(state.kit1);
        kit.available = false;
        setKit1(kit);
      }
      render();
    });

    // Drawer quantity changes hand over the fresh cart; no extra request.
    document.addEventListener('cart:updated', function (event) {
      var cart = event.detail && event.detail.cart;
      if (!cart || !Array.isArray(cart.items)) return;
      var units = [];
      cart.items.forEach(function (item) {
        if (!isEligibleLine(item, eligibility)) return;
        var price = item.original_price != null ? item.original_price : item.price;
        for (var i = 0; i < item.quantity; i += 1) units.push(parseInt(price, 10) || 0);
      });
      state.cartUnits = units;
      render();
    });

    // ---- Swap kit flow -----------------------------------------------------------

    var modal = el.modal;
    var dialog = modal && modal.querySelector('[data-mm3-dialog]');
    var listView = modal && modal.querySelector('[data-mm3-view="list"]');
    var qvView = modal && modal.querySelector('[data-mm3-view="qv"]');
    var qvBody = modal && modal.querySelector('[data-mm3-qv-body]');
    var useButton = modal && modal.querySelector('[data-mm3-use]');
    var fullLink = modal && modal.querySelector('[data-mm3-full]');
    var footer = modal && modal.querySelector('.mm3-modal__footer');
    var desktop = root.matchMedia ? root.matchMedia('(min-width: 750px)') : null;
    var lockedScroll = null;

    // "Use this kit" + the full-page link: in the info column on desktop (as in
    // the design), a sticky footer under the scrolling body on mobile.
    function placeFooter() {
      if (!footer) return;
      var info = qvBody.querySelector('.mm3-qv__info');
      var target = desktop && desktop.matches && info ? info : qvView;
      if (footer.parentNode !== target) target.appendChild(footer);
      if (typeof updateSliders === 'function') updateSliders();
    }

    // The quick view body is replaced on every open: keep the footer out of it.
    function setQuickViewBody(html) {
      if (footer && footer.parentNode !== qvView) qvView.appendChild(footer);
      qvBody.innerHTML = html;
      placeFooter();
      repaintBody(qvBody);
      refreshPaint(qvBody);
    }

    if (desktop) {
      if (desktop.addEventListener) desktop.addEventListener('change', placeFooter);
      else if (desktop.addListener) desktop.addListener(placeFooter);
    }

    // Okendo's own star widget (the fallback in mix-match-rating.liquid) only
    // renders once Okendo initialises it; markup injected later needs a nudge.
    function initReviews(scope) {
      var api = root.okeWidgetApi || root.okendoWidgetApi;
      if (!api || !scope.querySelector('[data-oke-star-rating]')) return;
      try {
        if (typeof api.initAllWidgets === 'function') api.initAllWidgets(scope);
        else if (typeof api.initWidget === 'function') {
          Array.prototype.forEach.call(scope.querySelectorAll('[data-oke-star-rating]'), function (node) {
            api.initWidget(node);
          });
        }
      } catch (e) {
        /* the server-rendered stars stay as they are */
      }
    }

    // Out of any transformed / overflow-hidden PDP container.
    if (modal) document.body.appendChild(modal);
    // A photo that finishes loading (new option, thumbnail) changes the
    // painted area: repaint (iOS blank areas). load doesn't bubble: capture.
    if (qvBody) {
      qvBody.addEventListener(
        'load',
        function (event) {
          if (event.target && event.target.tagName === 'IMG') refreshPaint(qvBody);
        },
        true
      );
    }

    /** Total of the kits being added with slot i replaced by `price`. */
    function totalWith(i, price) {
      var replaced = prices(state.slots);
      replaced[i] = price;
      return quote(pricing, replaced, state.cartUnits);
    }

    function deltaText(delta, variantTitle, withVariant) {
      if (delta === 0) return variantTitle ? fill(strings.noChangeWith, { variant: variantTitle }) : strings.noChange;
      var template = delta < 0
        ? withVariant && variantTitle ? strings.lessWith : strings.less
        : withVariant && variantTitle ? strings.moreWith : strings.more;
      return fill(template, { amount: money(Math.abs(delta)), variant: variantTitle });
    }

    function renderList() {
      var i = state.modalSlot;
      var slot = state.slots[i];
      var base = quoteSlots(state.slots);
      modal.querySelector('[data-mm3-list-title]').textContent = fill(strings.chooseKit, { n: i + 1, count: state.qty });
      modal.querySelector('[data-mm3-list-intro]').innerHTML = base.pct
        ? fill(strings.intro, { pct: '<strong>' + pctText(base.pct) + '</strong>' })
        : strings.introPlain;

      Array.prototype.forEach.call(modal.querySelectorAll('[data-mm3-pick]'), function (row) {
        var productId = row.getAttribute('data-product-id');
        var minPrice = parseInt(row.getAttribute('data-min-price'), 10) || 0;
        var minTitle = row.getAttribute('data-min-variant-title');
        var isCurrent = String(slot.productId) === productId;
        // The kit already in this slot is priced as it is; the others "From".
        var rowPrice = isCurrent ? slot.price : minPrice;
        var q = totalWith(i, rowPrice);
        row.querySelector('[data-mm3-row-current]').hidden = !isCurrent;
        var now = q.unitAfter[i];
        setText(row.querySelector('[data-mm3-row-was]'), now < rowPrice ? money(rowPrice) : '');
        var from = row.querySelector('.mm3-list__from');
        if (from) from.hidden = isCurrent;
        row.querySelector('[data-mm3-row-now]').textContent = money(now);
        var delta = row.querySelector('[data-mm3-row-delta]');
        var text;
        if (isCurrent) {
          text = !slot.swapped && i > 0 ? strings.sameAsKit1 : strings.noChange;
        } else {
          text = deltaText(q.total - base.total, minTitle, false);
        }
        delta.textContent = text;
        delta.classList.toggle('is-less', !isCurrent && q.total < base.total);
        row.setAttribute('aria-label', row.getAttribute('data-title') + ', ' + money(now) + ', ' + text);
      });
    }

    function showView(name) {
      listView.hidden = name !== 'list';
      qvView.hidden = name !== 'qv';
      var focusTarget = name === 'list'
        ? listView.querySelector('[data-mm3-pick]') || listView.querySelector('[data-mm3-close]')
        : qvView.querySelector('[data-mm3-back]');
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    }

    /*
     * The sheet's height from the VISIBLE viewport. On iOS Safari 100vh
     * includes the address and tool bars, so a 92vh sheet hid its title / ×
     * under the address bar and its "Use this kit" footer below the screen
     * until a refresh. --mm3-vh is the visible height in px, kept current
     * while the sheet is open (the bars grow and shrink as you scroll).
     */
    function sizeModal() {
      if (!modal) return;
      var vv = root.visualViewport;
      var h = vv && vv.height ? vv.height : root.innerHeight;
      if (h) modal.style.setProperty('--mm3-vh', Math.round(h) + 'px');
    }
    if (root.visualViewport) root.visualViewport.addEventListener('resize', function () {
      if (modal && !modal.hidden) sizeModal();
    });
    root.addEventListener('resize', function () {
      if (modal && !modal.hidden) sizeModal();
    });

    function openModal(i, trigger) {
      if (!modal || !state.slots[i]) return;
      sizeModal();
      state.modalSlot = i;
      state.modalTrigger = trigger;
      renderList();
      modal.hidden = false;
      lockedScroll = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      document.body.classList.add('mm3-modal-open');
      dialog.style.transform = '';
      showView('list');
    }

    function closeModal() {
      if (!modal || modal.hidden) return;
      modal.hidden = true;
      document.documentElement.style.overflow = lockedScroll || '';
      document.body.classList.remove('mm3-modal-open');
      state.qv = null;
      var trigger = state.modalTrigger;
      // The slot re-renders its button; return focus to the same slot's button.
      var again = trigger && widget.querySelector('[data-mm3-swap="' + state.modalSlot + '"]');
      (again || trigger || widget).focus({ preventScroll: true });
    }

    function loadQuickView(row) {
      var handle = row.getAttribute('data-mm3-pick');
      if (!quickViewCache[handle]) {
        var url = withParam(row.getAttribute('data-url'), 'section_id', config.quickViewSection);
        quickViewCache[handle] = fetch(url, { credentials: 'same-origin' })
          .then(function (response) {
            if (!response.ok) throw new Error(String(response.status));
            return response.text();
          })
          .then(function (html) {
            var holder = document.createElement('div');
            holder.innerHTML = html;
            var qv = holder.querySelector('[data-mm3-qv]');
            if (!qv) throw new Error('missing quick view');
            return qv.outerHTML;
          })
          .catch(function (error) {
            delete quickViewCache[handle];
            throw error;
          });
      }
      return quickViewCache[handle];
    }

    /*
     * The kit's own "Swap kit: options to show": the setting on the kit's own
     * PDP template, so a kit set up on its template ("Choose Your Paints:
     * Pastel Tones, …") is offered the same way from every other PDP.
     *
     * Read from the kit's own product page: its data-mm3-config is rendered
     * with the kit's assigned template. Not the Section Rendering API: a JSON
     * template section's id is "template--<template id>__main", unique per
     * template, so "?section_id=main" 404s and another template's id renders
     * THAT template's settings with the kit (wrong filter). The page is
     * streamed and the download cancelled as soon as the kit's config has
     * arrived (about half the page). Cached per handle, prefetched when a
     * row is hovered / touched / focused. No filter there, or the request
     * fails: {} (the block's own filter applies).
     */
    var configPattern = /<script type="application\/json" data-mm3-config>([\s\S]*?)<\/script>/g;

    function findKitFilter(html, handle) {
      configPattern.lastIndex = 0;
      var match;
      while ((match = configPattern.exec(html))) {
        try {
          var kitConfig = JSON.parse(match[1]);
          if (kitConfig && kitConfig.product && String(kitConfig.product.handle) === String(handle)) {
            return kitConfig.optionFilter || {};
          }
        } catch (e) {
          /* not ours */
        }
      }
      return null;
    }

    function fetchKitFilter(url, handle) {
      return fetch(url, { credentials: 'same-origin' }).then(function (response) {
        if (!response || !response.ok) throw new Error('kit page ' + (response && response.status));
        if (!response.body || !root.TextDecoder) {
          return response.text().then(function (html) {
            return findKitFilter(html, handle) || {};
          });
        }
        var reader = response.body.getReader();
        var decoder = new TextDecoder();
        var html = '';
        function pump() {
          return reader.read().then(function (chunk) {
            if (chunk.done) return findKitFilter(html + decoder.decode(), handle) || {};
            html += decoder.decode(chunk.value, { stream: true });
            // Only parse once a config script has fully arrived.
            if (html.indexOf('data-mm3-config') !== -1) {
              var found = findKitFilter(html, handle);
              if (found) {
                reader.cancel().catch(function () {});
                return found;
              }
            }
            return pump();
          });
        }
        return pump();
      });
    }

    function kitFilterPromise(row) {
      var handle = row.getAttribute('data-mm3-pick');
      if (config.product && String(config.product.handle) === String(handle)) {
        return Promise.resolve(config.optionFilter || {});
      }
      if (config.kitFilter === false) return Promise.resolve({});
      return filterForUrl(row.getAttribute('data-url'), handle);
    }

    /** A product's own "options to show" (cache, else its page); never rejects. */
    function filterForUrl(url, handle) {
      handle = handle || handleFromUrl(url);
      if (!handle) return Promise.resolve({});
      if (config.product && String(config.product.handle) === String(handle)) {
        return Promise.resolve(config.optionFilter || {});
      }
      var cached = readFilterCache(handle);
      if (cached) return Promise.resolve(cached);
      if (!kitFilterCache[handle]) {
        kitFilterCache[handle] = fetchKitFilter(url, handle)
          .then(function (filter) {
            writeFilterCache(handle, filter);
            return filter;
          })
          .catch(function () {
            delete kitFilterCache[handle];
            return {};
          });
      }
      return kitFilterCache[handle];
    }

    /*
     * Add-on dropdowns follow their product's own "options to show" too:
     * options it hides are removed; a hidden selection moves to the first
     * allowed one (change event, so the add-on state follows). A filter that
     * would leave nothing is ignored.
     */
    function filterAddonSelects() {
      Array.prototype.forEach.call(widget.querySelectorAll('select[data-mm3-filter-url]'), function (select) {
        var names;
        try {
          names = JSON.parse(select.getAttribute('data-mm3-option-names') || '[]');
        } catch (e) {
          return;
        }
        filterForUrl(select.getAttribute('data-mm3-filter-url')).then(function (filter) {
          var keys = Object.keys(filter || {}).filter(function (k) {
            return Array.isArray(filter[k]) && filter[k].length;
          });
          if (!keys.length) return;
          var options = Array.prototype.slice.call(select.options);
          var hide = options.filter(function (option) {
            var values;
            try {
              values = JSON.parse(option.getAttribute('data-mm3-options') || 'null');
            } catch (e) {
              values = null;
            }
            if (!values) return false;
            return !names.every(function (name, o) {
              var allowed = filter[name];
              return !allowed || !allowed.length || allowed.indexOf(values[o]) !== -1;
            });
          });
          if (!hide.length || hide.length === options.length) return;
          var lostSelection = hide.indexOf(select.options[select.selectedIndex]) !== -1;
          hide.forEach(function (option) {
            option.parentNode.removeChild(option);
          });
          if (lostSelection) {
            select.selectedIndex = 0;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
    }

    function loadKitFilter(row) {
      // Never hold the quick view up for long: after 8s open without it.
      return Promise.race([
        kitFilterPromise(row),
        new Promise(function (resolve) {
          setTimeout(function () {
            resolve({});
          }, 8000);
        })
      ]);
    }

    function openQuickView(row) {
      var i = state.modalSlot;
      setQuickViewBody('<p class="mm3-qv__loading">' + strings.loading + '</p>');
      modal.querySelector('[data-mm3-qv-step]').textContent = fill(strings.kitStep, { n: i + 1, count: state.qty });
      useButton.disabled = true;
      useButton.textContent = fill(strings.use, { amount: '' }).replace(/\s·\s$/, '');
      fullLink.href = row.getAttribute('data-url');
      showView('qv');

      Promise.all([loadQuickView(row), loadKitFilter(row)]).then(
        function (results) {
          var html = results[0];
          if (state.modalSlot !== i || qvView.hidden) return;
          setQuickViewBody(html);
          initReviews(qvBody);
          var data;
          try {
            data = JSON.parse(qvBody.querySelector('[data-mm3-qv-data]').textContent);
          } catch (e) {
            setQuickViewBody('<p class="mm3-qv__loading">' + strings.loadError + '</p>');
            return;
          }
          var slot = state.slots[i];
          data.kitFilter = results[1] || {};
          data.allowed = allowedVariants(data);
          var start = String(slot.productId) === String(data.id) ? slot.variantId : null;
          var startVariant = null;
          data.allowed.forEach(function (v) {
            if (start && String(v.id) === String(start)) startVariant = v;
          });
          if (!startVariant) {
            data.allowed.forEach(function (v) {
              if (!startVariant && v.available) startVariant = v;
            });
          }
          startVariant = startVariant || data.allowed[0];
          state.qv = { data: data, selected: startVariant.options.slice(), variant: startVariant };
          renderQuickView();
          centreSelectedSwatches();
        },
        function () {
          if (state.modalSlot === i) setQuickViewBody('<p class="mm3-qv__loading">' + strings.loadError + '</p>');
        }
      );
    }

    /*
     * Name a variant by the options that set its price: "Two Candles", not
     * "Pastel Tones / Two Candles / Peony Rose". An option sets the price when
     * two variants differing only in it cost differently.
     */
    function priceName(data, variant) {
      if (!data.priceOptions) {
        var relevant = [];
        var count = (data.variants[0] && data.variants[0].options.length) || 0;
        for (var o = 0; o < count; o += 1) {
          relevant[o] = data.variants.some(function (a) {
            return data.variants.some(function (b) {
              if (a.price === b.price || a.options[o] === b.options[o]) return false;
              for (var k = 0; k < count; k += 1) if (k !== o && a.options[k] !== b.options[k]) return false;
              return true;
            });
          });
        }
        data.priceOptions = relevant;
      }
      var parts = variant.options.filter(function (value, o) {
        return data.priceOptions[o];
      });
      return parts.length ? parts.join(' / ') : variant.title;
    }

    /*
     * "Swap kit: options to show": only these values of these options are
     * offered in the quick view, e.g. the 4 tone paints. The kit's own setting
     * (its PDP template, data.kitFilter) decides the options it lists; this
     * block's setting covers the rest. Option names and values match exactly;
     * a filter that would leave a kit with nothing to choose is ignored.
     */
    function allowedVariants(data) {
      var filter = {};
      var own = config.optionFilter || {};
      var kit = data.kitFilter || {};
      Object.keys(own).forEach(function (name) {
        filter[name] = own[name];
      });
      Object.keys(kit).forEach(function (name) {
        if (Array.isArray(kit[name]) && kit[name].length) filter[name] = kit[name];
      });
      var names = data.options || [];
      var list = data.variants.filter(function (v) {
        return names.every(function (name, o) {
          var allowed = filter[name];
          return !allowed || allowed.indexOf(v.options[o]) !== -1;
        });
      });
      return list.length ? list : data.variants;
    }

    // Sliders in the quick view: the thumbnails (desktop + mobile) and the
    // image swatches (mobile, like the PDP's). Arrows only when the row
    // overflows; greyed out at either end.
    function updateSliders() {
      Array.prototype.forEach.call(qvBody.querySelectorAll('[data-mm3-slider]'), function (slider) {
        var track = slider.querySelector('[data-mm3-track]');
        var prev = slider.querySelector('[data-mm3-slide="-1"]');
        var next = slider.querySelector('[data-mm3-slide="1"]');
        if (!track || !prev || !next) return;
        var overflow = track.scrollWidth > track.clientWidth + 1;
        prev.hidden = !overflow;
        next.hidden = !overflow;
        prev.disabled = track.scrollLeft <= 4;
        next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      });
    }

    function centreSelectedSwatches() {
      Array.prototype.forEach.call(qvBody.querySelectorAll('.mm3-opt--swatch [data-mm3-track]'), function (track) {
        var checked = track.querySelector('.mm3-opt__input:checked');
        var item = checked && checked.closest('[data-mm3-value-wrap]');
        // Only when the selected swatch is out of view: otherwise stay at the start.
        if (item && track.scrollWidth > track.clientWidth && item.offsetLeft + item.offsetWidth > track.clientWidth) {
          track.scrollLeft = Math.max(0, item.offsetLeft - (track.clientWidth - item.offsetWidth) / 2);
        }
      });
      updateSliders();
    }

    function findVariant(data, selected) {
      for (var v = 0; v < data.variants.length; v += 1) {
        var variant = data.variants[v];
        var match = true;
        for (var o = 0; o < selected.length; o += 1) {
          if (variant.options[o] !== selected[o]) {
            match = false;
            break;
          }
        }
        if (match) return variant;
      }
      return null;
    }

    /*
     * Sold-out values, like the theme's hide-sold-variants.js on the PDP: a
     * value is hidden when every variant with it (and the options chosen
     * before it) is sold out, at any option level: no Fragrance that is sold
     * out for "Two Candles" once Two Candles is chosen. Except variants with
     * the "exclude_from_hide" metafield (theme setting "Allow Variant
     * Exclusions"): shown greyed out. Theme setting "Hide Sold Out Variants"
     * off: sold-out values stay, disabled.
     *   'ok' | 'excluded' | 'soldout' | 'hidden'
     */
    function valueState(data, allowed, selected, index, value) {
      var matching = allowed.filter(function (v) {
        for (var k = 0; k < index; k += 1) if (v.options[k] !== selected[k]) return false;
        return v.options[index] === value;
      });
      if (!matching.length) return 'hidden';
      if (matching.some(function (v) { return v.available; })) return 'ok';
      if (data.hideSold === false) return 'soldout';
      if (data.allowExcluded !== false && matching.some(function (v) { return v.exclude; })) return 'excluded';
      return 'hidden';
    }

    // Values in the quick view's order (the product's option order).
    function optionValues(index) {
      return Array.prototype.map.call(qvBody.querySelectorAll('[data-mm3-option="' + index + '"] [data-mm3-value-wrap]'), function (wrap) {
        return wrap.getAttribute('data-mm3-value');
      });
    }

    // A chosen value that is hidden now (an earlier option changed) moves to
    // the first value that can be bought, like the PDP.
    function settleSelection(data, allowed, selected) {
      for (var o = 0; o < selected.length; o += 1) {
        if (valueState(data, allowed, selected, o, selected[o]) !== 'hidden') continue;
        var values = optionValues(o);
        var pick = null;
        ['ok', 'excluded', 'soldout'].some(function (wanted) {
          values.some(function (value) {
            if (valueState(data, allowed, selected, o, value) === wanted) pick = value;
            return pick !== null;
          });
          return pick !== null;
        });
        if (pick !== null) selected[o] = pick;
      }
      Array.prototype.forEach.call(qvBody.querySelectorAll('[data-mm3-input]'), function (input) {
        input.checked = input.value === selected[parseInt(input.getAttribute('data-mm3-input'), 10)];
      });
    }

    function renderQuickView() {
      var qv = state.qv;
      if (!qv) return;
      var data = qv.data;
      var i = state.modalSlot;
      var allowed = data.allowed || data.variants;
      settleSelection(data, allowed, qv.selected);
      var variant = findVariant({ variants: allowed }, qv.selected);
      qv.variant = variant;
      var base = quoteSlots(state.slots);

      // Options: selected label, sold-out / hidden values, price-changing pills.
      Array.prototype.forEach.call(qvBody.querySelectorAll('[data-mm3-option]'), function (wrap) {
        var index = parseInt(wrap.getAttribute('data-mm3-option'), 10);
        var selectedLabel = wrap.querySelector('[data-mm3-option-selected]');
        if (selectedLabel) selectedLabel.textContent = qv.selected[index];
        var valuePrices = {};
        var priceSet = {};
        Array.prototype.forEach.call(wrap.querySelectorAll('[data-mm3-value-wrap]'), function (valueWrap) {
          var value = valueWrap.getAttribute('data-mm3-value');
          var probe = qv.selected.slice();
          probe[index] = value;
          var match = findVariant({ variants: allowed }, probe);
          // See valueState. Values outside "Swap kit: options to show" are hidden too.
          var status = valueState(data, allowed, qv.selected, index, value);
          valueWrap.hidden = status === 'hidden';
          var unavailable = status === 'excluded' || status === 'soldout';
          valueWrap.classList.toggle('disabled', unavailable);
          valueWrap.classList.toggle('is-excluded', status === 'excluded');
          var label = valueWrap.querySelector('label');
          if (label) label.classList.toggle('disabled', unavailable);
          if (match) {
            valuePrices[value] = match.price;
            priceSet[match.price] = true;
          }
        });
        var varies = Object.keys(priceSet).length > 1;
        Array.prototype.forEach.call(wrap.querySelectorAll('[data-mm3-value-wrap]'), function (valueWrap) {
          var priceEl = valueWrap.querySelector('[data-mm3-value-price]');
          if (!priceEl) return;
          var value = valueWrap.getAttribute('data-mm3-value');
          priceEl.textContent = varies && valuePrices[value] != null ? ' · ' + money(valuePrices[value]).replace(/[.,]00(?=\D*$)/, '') : '';
        });
      });

      var soldOut = qvBody.querySelector('[data-mm3-qv-soldout]');
      var buyable = !!variant && variant.available;
      if (soldOut) soldOut.hidden = buyable;
      var wasEl = qvBody.querySelector('[data-mm3-qv-was]');
      var nowEl = qvBody.querySelector('[data-mm3-qv-now]');
      var pillEl = qvBody.querySelector('[data-mm3-qv-pill]');
      var deltaEl = qvBody.querySelector('[data-mm3-qv-delta]');

      if (!variant) {
        useButton.disabled = true;
        setText(deltaEl, '');
        return;
      }
      var q = totalWith(i, variant.price);
      var now = q.unitAfter[i];
      setText(wasEl, now < variant.price ? money(variant.price) : '');
      nowEl.textContent = money(now);
      setText(pillEl, q.pct ? fill(strings.pillOff, { pct: pctText(q.pct), n: i + 1 }) : '');
      var defaultOnly = data.variants.length === 1;
      var delta = q.total - base.total;
      setText(deltaEl, deltaText(delta, defaultOnly ? '' : priceName(data, variant), true));
      deltaEl.classList.toggle('is-less', delta < 0);

      var main = qvBody.querySelector('.mm3-qv__main-img');
      if (main && variant.imageLarge && qv.imageVariant !== variant.id) {
        qv.imageVariant = variant.id;
        main.setAttribute('src', variant.imageLarge);
        main.removeAttribute('srcset');
      }

      useButton.disabled = !buyable;
      useButton.textContent = buyable ? fill(strings.use, { amount: money(now) }) : strings.soldOut;
      fullLink.href = withParam(data.url, 'variant', variant.id);
      refreshPaint(qvBody);
    }

    function useKit() {
      var qv = state.qv;
      if (!qv || !qv.variant || !qv.variant.available) return;
      var data = qv.data;
      var variant = qv.variant;
      var i = state.modalSlot;
      var sameAsKit1 = String(data.id) === String(state.kit1.productId) && String(variant.id) === String(state.kit1.variantId);
      state.slots[i] = sameAsKit1
        ? copy(state.kit1)
        : {
            productId: data.id,
            handle: data.handle,
            title: data.title,
            url: data.url,
            defaultOnly: data.variants.length === 1,
            variantId: variant.id,
            variantTitle: variant.title,
            price: variant.price,
            image: variant.image || data.image,
            available: true,
            swapped: true,
          };
      members[String(data.id)] = true;
      if (state.qty >= 2) state.persist = true;
      render();
      closeModal();
    }

    if (modal) {
      ['pointerenter', 'touchstart', 'focusin'].forEach(function (type) {
        modal.addEventListener(
          type,
          function (event) {
            var row = event.target && event.target.closest && event.target.closest('[data-mm3-pick]');
            if (row) kitFilterPromise(row);
          },
          { capture: true, passive: true }
        );
      });
      modal.addEventListener('click', function (event) {
        if (event.target.closest('[data-mm3-close]')) {
          closeModal();
          return;
        }
        if (event.target.closest('[data-mm3-back]')) {
          state.qv = null;
          renderList();
          showView('list');
          return;
        }
        var row = event.target.closest('[data-mm3-pick]');
        if (row) {
          openQuickView(row);
          return;
        }
        var thumb = event.target.closest('[data-mm3-qv-thumb]');
        if (thumb) {
          var main = qvBody.querySelector('.mm3-qv__main-img');
          if (main) {
            main.setAttribute('src', thumb.getAttribute('data-mm3-qv-thumb'));
            main.removeAttribute('srcset');
          }
          Array.prototype.forEach.call(qvBody.querySelectorAll('[data-mm3-qv-thumb]'), function (t) {
            t.classList.toggle('is-active', t === thumb);
          });
          // Keep the chosen thumbnail fully in view in the slider.
          var thumbTrack = thumb.closest('[data-mm3-track]');
          if (thumbTrack) {
            var left = thumb.offsetLeft - thumbTrack.offsetLeft;
            if (left < thumbTrack.scrollLeft) thumbTrack.scrollLeft = left;
            else if (left + thumb.offsetWidth > thumbTrack.scrollLeft + thumbTrack.clientWidth) {
              thumbTrack.scrollLeft = left + thumb.offsetWidth - thumbTrack.clientWidth;
            }
          }
          return;
        }
        var slide = event.target.closest('[data-mm3-slide]');
        if (slide) {
          var track = slide.closest('[data-mm3-slider]').querySelector('[data-mm3-track]');
          var step = Math.max(track.clientWidth * 0.75, 70) * parseInt(slide.getAttribute('data-mm3-slide'), 10);
          if (track.scrollBy) track.scrollBy({ left: step, behavior: 'smooth' });
          else track.scrollLeft += step;
          return;
        }
        if (event.target.closest('[data-mm3-use]')) useKit();
        // "View full product page": keep the kit being viewed in its slot (as
        // "Use this kit" does), so the new tab finds it in the saved selection.
        // The link still opens normally.
        if (event.target.closest('[data-mm3-full]') && state.qty >= 2) {
          state.persist = true;
          useKit();
        }
      });

      // Scroll doesn't bubble: listen in the capture phase for the swatch rows.
      var sliderFrame = null;
      modal.addEventListener(
        'scroll',
        function (event) {
          if (!event.target.matches || !event.target.matches('[data-mm3-track]')) return;
          if (sliderFrame) return;
          sliderFrame = (root.requestAnimationFrame || root.setTimeout)(function () {
            sliderFrame = null;
            updateSliders();
          });
        },
        true
      );

      modal.addEventListener('change', function (event) {
        var input = event.target.closest('[data-mm3-input]');
        if (!input || !state.qv) return;
        state.qv.selected[parseInt(input.getAttribute('data-mm3-input'), 10)] = input.value;
        renderQuickView();
      });

      // Esc closes; Tab stays inside the dialog.
      modal.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeModal();
          return;
        }
        if (event.key !== 'Tab') return;
        var focusable = Array.prototype.filter.call(
          dialog.querySelectorAll('button, [href], input, [tabindex]:not([tabindex="-1"])'),
          function (node) {
            return !node.disabled && node.offsetParent !== null && !node.closest('[hidden]');
          }
        );
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });

      // Mobile bottom sheet: drag the handle down to close.
      var handle = modal.querySelector('[data-mm3-drag]');
      var dragStart = null;
      handle.addEventListener('pointerdown', function (event) {
        dragStart = event.clientY;
        handle.setPointerCapture(event.pointerId);
      });
      handle.addEventListener('pointermove', function (event) {
        if (dragStart == null) return;
        var dy = Math.max(0, event.clientY - dragStart);
        dialog.style.transform = dy ? 'translateY(' + dy + 'px)' : '';
      });
      handle.addEventListener('pointerup', function (event) {
        if (dragStart == null) return;
        var dy = event.clientY - dragStart;
        dragStart = null;
        dialog.style.transform = '';
        if (dy > 80) closeModal();
      });
    }

    // Another tab (the PLP "Your kits", or another kit page) saved or cleared
    // the bundle: show the same kits here. localStorage.clear() sends key null.
    root.addEventListener('storage', function (event) {
      if (!document.documentElement.contains(widget)) return;
      if (event.key !== null && event.key !== STORE_PREFIX + config.group) return;
      syncSelection();
    });
    // Same tab: the announcement bar added the saved bundle to the cart, etc.
    // Our own writes are recognised (seenSet / seenExtras) and skipped.
    document.addEventListener('mm3:selection', function () {
      if (!document.documentElement.contains(widget)) return;
      syncSelection();
    });
    // Back / forward from the PLP or another kit page: this page comes from
    // the browser's cache, with the bundle as it was when we left.
    root.addEventListener('pageshow', function (event) {
      if (!event.persisted || !document.documentElement.contains(widget)) return;
      syncSelection();
    });

    // ---- Start ---------------------------------------------------------------

    state.kit1 = initialKit1();
    state.slots = [copy(state.kit1)];
    if (config.product) writeFilterCache(config.product.handle, config.optionFilter || {});
    restoreSelection();
    placeBuyButtons();
    placeAfterButton();
    render();
    filterAddonSelects();

    // The sticky bar can render, or rewrite its label, after this script runs.
    root.addEventListener('load', function () {
      root.setTimeout(function () {
        placeBuyButtons();
        placeAfterButton();
        render();
      }, 600);
    });
  }

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-mm3]'), init);
  }

  root.SculpdMixMatch = {
    pricing: { tierFor: tierFor, unitOff: unitOff, quote: quote },
    isEligibleLine: isEligibleLine,
    formatMoney: formatMoney,
  };

  if (typeof document === 'undefined') return;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})(typeof window !== 'undefined' ? window : globalThis);
